import { useContext } from "react";
import { useSelector } from "react-redux";

import { MedplumClient } from "@medplum/core";
import { DocumentReference } from "@medplum/fhirtypes";

import { Button, Group, Paper, Stack } from "@mantine/core";

import { IconFileX, IconPlus } from "@tabler/icons-react";

import { TextWrapper } from "@/components/TextWrapper";
import { ProfileContext } from "@/contexts/profile";
import { NoDataContainer } from "@/encounters/components/NoDataContainer";
import { usePractitionerPermissions } from "@/permission/hooks/usePractitionerPermissions";
import { RootState, useAppDispatch } from "@/store";
import { selectFilesForEncounter } from "@/store/selectors";
import { deleteFileThunk } from "@/store/slices/uploaded-files";

import FileRow from "./components/FileRow/FileRow";
import { useFileContext } from "./contexts/FileUploadContext";

interface FilesProps<T extends string> {
  open: (id: T) => void;
  isEncounterActive: boolean;
  isAmendment: boolean;
  medplum: MedplumClient;
  clientId: string;
  encounterId: string;
  filesDrawerId: T;
}

export function Files<T extends string>({
  open,
  isEncounterActive,
  isAmendment,
  medplum,
  clientId,
  encounterId,
  filesDrawerId,
}: FilesProps<T>) {
  const dispatch = useAppDispatch();
  const { hasProviderOrScribeRole } = usePractitionerPermissions();
  const { isPatient, profile } = useContext(ProfileContext);
  const { setSelectedFile } = useFileContext();
  const files = useSelector((state: RootState) =>
    selectFilesForEncounter(state, clientId, encounterId),
  );

  const handleOpenUploadDrawer = () => {
    setSelectedFile(undefined);
    open(filesDrawerId);
  };

  const handleEditFile = (file: DocumentReference) => {
    setSelectedFile(file);
    open(filesDrawerId);
  };

  const handleDeleteFile = (file: DocumentReference) => {
    dispatch(deleteFileThunk({ medplum, file }));
  };

  const hasFiles = files.length > 0;
  const isPatientViewingOwnFiles = isPatient && profile?.id === clientId;
  // Patients can upload files but not edit/delete them
  const canUpload =
    (isEncounterActive || isAmendment) && (hasProviderOrScribeRole || isPatientViewingOwnFiles);

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <TextWrapper c="dark.9" variant="bold">
          Files
        </TextWrapper>
        {canUpload && hasFiles && (
          <Button
            variant="outline"
            color="burgundy.9"
            onClick={handleOpenUploadDrawer}
            leftSection={<IconPlus size={16} />}
          >
            Add More
          </Button>
        )}
      </Group>
      <Paper withBorder radius="md" style={{ background: "white", padding: 16 }}>
        {hasFiles ? (
          <Stack gap="md">
            {files.map((documentReference, index) => (
              <FileRow
                key={index}
                medplum={medplum}
                onEdit={handleEditFile}
                isAmendment={isAmendment}
                onDelete={handleDeleteFile}
                documentReference={documentReference}
                isEncounterActive={isEncounterActive}
              />
            ))}
          </Stack>
        ) : (
          <NoDataContainer
            text="No files have been added."
            hideAnchor={!isEncounterActive && !isAmendment}
            linkText={canUpload ? "Add File to Library" : ""}
            onAnchorClick={canUpload ? handleOpenUploadDrawer : () => {}}
            icon={<IconFileX size={18} color="var(--mantine-color-gray-6)" />}
          />
        )}
      </Paper>
    </Stack>
  );
}
